<template>
    <div class="global-selector-container">
        <div class="is-size-7 is-flex is-flex-direction-row has-text-weight-semibold mb-1">
            {{ label }}
        </div>
        <div class="control">
            <div class="multi-select-container">
                <div class="multi-select-dropdown" @click="toggleDropdown" @click.stop
                    :class="{ 'is-active': isDropdownOpen, 'disabled': disabled }">
                    <div class="multi-select-display">
                        <span v-if="displayValue === ''" class="placeholder">{{ placeholder }}</span>
                        <span v-else class="selected-text">
                            {{ displayValue }}
                        </span>
                    </div>
                    <div class="dropdown-controls">
                        <!-- Clear button for single select -->
                        <button v-if="!isMultiSelect && displayValue !== '' && !disabled" class="clear-button"
                            @click.stop="clearSelection" type="button" title="Clear selection">
                            ✕
                        </button>
                        <span class="icon is-small">
                            <i class="fas fa-chevron-down"></i>
                        </span>
                    </div>
                </div>

                <div v-if="isDropdownOpen" class="multi-select-options" :key="dropdownKey">
                    <div v-if="showSearch" class="multi-select-search">
                        <div class="is-flex is-align-items-center">
                            <input class="input is-small" type="text" v-model="searchTerm"
                                :placeholder="`Search ${label.toLowerCase()}...`" @click.stop style="flex: 1;" />
                            <button v-if="isMultiSelect" class="button is-small is-success ml-2"
                                @click.stop="selectAllFiltered">
                                Select All
                            </button>
                            <button v-if="isMultiSelect" class="button is-small is-light ml-2" @click.stop="clearAll">
                                Clear All
                            </button>
                        </div>
                    </div>
                    <div class="multi-select-list">
                        <div v-for="option in filteredOptions" :key="getOptionKey(option)" class="multi-select-option">
                            <input type="checkbox" :value="getOptionValue(option)" :checked="isOptionSelected(option)"
                                :key="`checkbox-${getOptionKey(option)}`" @click.stop="handleOptionClick(option)" />
                            <span class="option-text" @click.stop="handleOptionClick(option)">{{ getOptionLabel(option) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Selected items display for multi-select -->
            <div v-if="isMultiSelect && selectedValue?.length > 0" class="mt-2">
                <div class="is-size-7 has-text-weight-semibold mb-1">Selected {{ label }}:</div>
                <div class="selected-items-container">
                    <div class="tags">
                        <span v-for="item in selectedValue" :key="item" class="tag is-small is-success">
                            {{ getDisplayLabel(item) }}
                            <button v-if="!disabled" class="delete is-small" @click="removeItem(item)"></button>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import store from "../store";

export default {
    name: 'GlobalSelector',
    props: {
        /**
         * Selected value(s) - single value for single select, array for multi-select
         */
        value: {
            type: [String, Number, Array, Object],
            default: () => []
        },
        /**
         * Label for the selector
         */
        label: {
            type: String,
            required: true
        },
        /**
         * Placeholder text
         */
        placeholder: {
            type: String,
            default: 'Select...'
        },
        /**
         * Whether the selector is disabled
         */
        disabled: {
            type: Boolean,
            default: false
        },
        /**
         * Whether to show search functionality
         */
        showSearch: {
            type: Boolean,
            default: true
        },
        /**
         * Type of selector - 'single' or 'multi'
         */
        type: {
            type: String,
            default: 'single',
            validator: (value) => ['single', 'multi'].includes(value)
        },
        /**
         * Data source type - 'zones', 'clients', 'hubs', 'contractors', 'custom'
         */
        dataSource: {
            type: String,
            required: true,
            validator: (value) => ['zones', 'clients', 'hubs', 'contractors', 'custom'].includes(value)
        },
        /**
         * Custom options array (used when dataSource is 'custom')
         */
        options: {
            type: Array,
            default: () => []
        },
        /**
         * Custom option key field (used when dataSource is 'custom')
         */
        optionKey: {
            type: String,
            default: 'id'
        },
        /**
         * Custom option label field (used when dataSource is 'custom')
         */
        optionLabel: {
            type: String,
            default: 'label'
        },
        /**
         * Custom option value field (used when dataSource is 'custom')
         */
        optionValue: {
            type: String,
            default: 'value'
        },
        /**
         * Zone type filter (used when dataSource is 'zones')
         */
        zoneType: {
            type: String,
            default: 'postalCode'
        },
        /**
         * Whether to include region filtering for hubs
         */
        filterByRegion: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            isDropdownOpen: false,
            searchTerm: "",
            selectedValue: this.value, // Initialize with prop value
            selectionVersion: 0, // Track selection changes for reactivity
        };
    },
    computed: {
        /**
         * Whether this is a multi-select
         */
        isMultiSelect() {
            return this.type === 'multi';
        },
        /**
         * Available options based on data source
         */
        availableOptions() {
            const currentRegion = store.state.region;
            const hubs = store.state.hubs || [];
            const filteredHubs = this.filterByRegion && currentRegion ? hubs.filter(hub => hub.Region === currentRegion) : hubs;

            let options = [];
            switch (this.dataSource) {
                case 'zones':
                    options = store.state.zoneInformations?.filter(zone => zone.type === this.zoneType).map((zone) => ({
                        value: zone.value.trim(),
                        label: zone.value.trim(),
                        key: zone.id.trim(),
                        zoneNameId: zone.id.trim(),
                        // For zones, we need both value and zoneNameId for compatibility
                        originalData: zone
                    })) || [];
                    break;
                case 'clients':
                    options = (store.state.vendors || []).map(vendor => ({
                        value: vendor.Id,
                        label: `${vendor.businessUnit}***${vendor.subBusinessUnit}`,
                        key: vendor.Id,
                        originalData: vendor
                    }));
                    break;
                case 'hubs':
                    options = filteredHubs.map(hub => ({
                        value: hub.DistributionZoneId,
                        label: hub.DistributionZoneName,
                        key: hub.DistributionZoneId,
                        // For hubs, we need the full hub object for compatibility
                        originalData: hub
                    }));
                    break;
                case 'contractors':
                    options = (store.state.contractors || []).map(contractor => ({
                        value: contractor.id,
                        label: contractor.company_name,
                        key: contractor.id,
                        originalData: contractor
                    }));
                    break;
                case 'custom':
                    options = this.options;
                    break;
                default:
                    options = [];
            }

            return options;
        },
        /**
         * Filtered options based on search term
         */
        filteredOptions() {
            if (!this.searchTerm) return this.availableOptions;

            return this.availableOptions.filter(option => {
                const label = this.getOptionLabel(option).toLowerCase();
                return label.includes(this.searchTerm.toLowerCase());
            });
        },
        /**
         * Display value for the dropdown
         */
        displayValue() {
            if (this.isMultiSelect) {
                if (!this.selectedValue || this.selectedValue.length === 0) {
                    return '';
                }
                if (this.selectedValue.length === 1) {
                    return this.getDisplayLabel(this.selectedValue[0]);
                }
                return `${this.selectedValue.length} items selected`;
            } else {
                if (!this.selectedValue) return '';

                // Handle different data structures for single select
                switch (this.dataSource) {
                    case 'zones':
                        return this.selectedValue.value || '';
                    case 'hubs':
                        return this.selectedValue.DistributionZoneName || '';
                    default:
                        return this.getDisplayLabel(this.selectedValue);
                }
            }
        },
        /**
         * Reactive key for dropdown options to ensure re-rendering
         */
        dropdownKey() {
            return `dropdown-${this.isDropdownOpen}-${this.dataSource}-${this.type}`;
        }
    },
    watch: {
        /**
         * Watch for changes in the prop value and update the local selectedValue
         */
        value(newVal) {
            this.selectedValue = newVal;
            // Increment selection version to force reactivity
            this.selectionVersion++;
        }
    },
    mounted() {
        // Add click outside listener to close dropdown
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        // Remove click outside listener
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        /**
         * Get option key
         */
        getOptionKey(option) {
            if (this.dataSource === 'custom') {
                return option[this.optionKey];
            }
            return option.key || option.value;
        },
        /**
         * Get option value
         */
        getOptionValue(option) {
            if (this.dataSource === 'custom') {
                return option[this.optionValue];
            }
            return option.value;
        },
        /**
         * Get option label
         */
        getOptionLabel(option) {
            if (this.dataSource === 'custom') {
                return option[this.optionLabel];
            }
            if (this.dataSource === 'clients') {
                const clientSplit = option.label.split('***');
                if (clientSplit[0] === clientSplit[1]) {
                    return clientSplit[0];
                }
                return option.label;
            }
            return option.label;
        },
        /**
         * Get display label for a selected value
         */
        getDisplayLabel(value) {
            // Handle different data structures for single select
            if (!this.isMultiSelect) {
                switch (this.dataSource) {
                    case 'zones':
                        if (value && value.value) {
                            return value.value;
                        }
                        break;
                    case 'hubs':
                        if (value && value.DistributionZoneName) {
                            return value.DistributionZoneName;
                        }
                        break;
                }
            }

            // For multi-select or fallback, find the option
            const option = this.availableOptions.find(opt => this.getOptionValue(opt) === value);
            return option ? this.getOptionLabel(option) : value;
        },
        /**
         * Check if an option is selected
         */
        isOptionSelected(option) {
            // Access selectionVersion to make this method reactive
            // this.selectionVersion;

            const optionValue = this.getOptionValue(option);

            if (this.isMultiSelect) {
                // For multi-select, selectedValue should be an array of values
                if (!this.selectedValue || !Array.isArray(this.selectedValue)) {
                    return false;
                }
                return this.selectedValue.includes(optionValue);
            } else {
                // Handle different data structures for single select
                switch (this.dataSource) {
                    case 'zones':
                        return this.selectedValue && this.selectedValue.value === optionValue;
                    case 'hubs':
                        return this.selectedValue && this.selectedValue.DistributionZoneId === optionValue;
                    default:
                        return this.selectedValue === optionValue;
                }
            }
        },
        /**
         * Handle option click for both single and multi-select
         */
        handleOptionClick(option) {
            if (this.disabled) return;
            const optionValue = this.getOptionValue(option);

            if (this.isMultiSelect) {
                // Multi-select: toggle the option
                // Ensure selectedValue is always an array
                const currentSelected = Array.isArray(this.selectedValue) ? this.selectedValue : [];

                if (currentSelected.includes(optionValue)) {
                    this.selectedValue = [...new Set(currentSelected.filter(val => val !== optionValue))];
                } else {
                    this.selectedValue = [...new Set([...currentSelected, optionValue])];
                }

                // Increment selection version to force reactivity
                this.selectionVersion++;
                this.$emit('input', this.selectedValue);
            } else {
                // Single-select: replace the current selection and close dropdown
                if (this.selectedValue === optionValue) {
                    this.selectedValue = null;
                } else {
                    // Emit the correct data structure based on data source
                    switch (this.dataSource) {
                        case 'zones':
                            // For zones, emit object with value and zoneNameId
                            this.selectedValue = {
                                value: option.value,
                                zoneNameId: option.zoneNameId
                            };
                            break;
                        case 'hubs':
                            // For hubs, emit the full hub object
                            this.selectedValue = option.originalData;
                            break;
                        default:
                            // For other data sources, emit the value
                            this.selectedValue = optionValue;
                    }
                }
                this.isDropdownOpen = false;
                this.searchTerm = "";
                this.$emit('input', this.selectedValue);
            }
        },
        /**
         * Remove an item from multi-select
         */
        removeItem(item) {
            if (this.disabled) return;

            if (this.isMultiSelect) {
                const currentSelected = Array.isArray(this.selectedValue) ? this.selectedValue : [];
                this.selectedValue = currentSelected.filter(id => id !== item);
                // Increment selection version to force reactivity
                this.selectionVersion++;
                // Emit the updated value
                this.$emit('input', this.selectedValue);
            }
        },
        /**
         * Clear all selected items
         */
        clearAll() {
            if (this.disabled) return;

            this.selectedValue = this.isMultiSelect ? [] : null;
            // Increment selection version to force reactivity
            this.selectionVersion++;
            // Emit the updated value
            this.$emit('input', this.selectedValue);
        },
        /**
         * Select all filtered items
         */
        selectAllFiltered() {
            if (this.disabled || !this.isMultiSelect) return;

            const filteredValues = this.filteredOptions.map(option => this.getOptionValue(option));
            const existingSelected = Array.isArray(this.selectedValue) ? this.selectedValue : [];

            // Merge existing selections with filtered items, avoiding duplicates
            this.selectedValue = [...new Set([...existingSelected, ...filteredValues])];
            // Increment selection version to force reactivity
            this.selectionVersion++;
            // Emit the updated value
            this.$emit('input', this.selectedValue);
        },
        /**
         * Toggle the dropdown open/closed state
         */
        toggleDropdown() {
            if (this.disabled) return;

            this.isDropdownOpen = !this.isDropdownOpen;

            if (this.isDropdownOpen) {
                // Focus on search input when opening
                this.$nextTick(() => {
                    const searchInput = this.$el.querySelector('.multi-select-search input');
                    if (searchInput) {
                        searchInput.focus();
                    }
                });
            } else {
                this.searchTerm = ""; // Clear search term when closing
            }
        },
        /**
         * Clear the current selection for single select
         */
        clearSelection() {
            if (this.disabled) return;
            this.selectedValue = null;
            // Emit the updated value
            this.$emit('input', this.selectedValue);
            this.isDropdownOpen = false;
            this.searchTerm = "";
        },
        /**
         * Handle clicks outside the dropdown to close it
         */
        handleClickOutside(event) {
            const dropdown = this.$el?.querySelector('.multi-select-dropdown');
            const options = this.$el?.querySelector('.multi-select-options');
            if (dropdown && options && !dropdown.contains(event.target) && !options.contains(event.target)) {
                this.isDropdownOpen = false;
                this.searchTerm = ""; // Clear search term when dropdown is closed
            }
        }
    }
};
</script>

<style scoped>
/* Multi-select styles */
.global-selector-container {
    width: 100%;
    max-width: 100%;
    overflow: visible;
    position: relative;
}

.multi-select-container {
    position: relative;
    width: 100%;
    max-width: 100%;
    overflow: visible;
}

.multi-select-dropdown {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.75rem;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    cursor: pointer;
    background-color: #fff;
    transition: all 0.2s ease-in-out;
    min-height: 2.25rem;
    height: 2.25rem;
    /* Fixed height to match other form elements */
    font-size: 0.875rem;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.multi-select-dropdown:hover {
    border-color: #b5b5b5;
}

.multi-select-dropdown.is-active {
    border-color: #3273dc;
    box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
}

.multi-select-dropdown.disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #f5f5f5;
}

.multi-select-display {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    margin-right: 0.5rem;
    min-width: 0;
    /* Allow flex item to shrink below content size */
    max-width: calc(100% - 2rem);
    /* Account for the chevron icon and clear button */
}

.multi-select-display .placeholder {
    color: #7a7a7a;
    font-style: italic;
}

.multi-select-display .selected-text {
    font-weight: 500;
    color: #363636;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.dropdown-controls {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
}

.clear-button {
    background: none;
    border: none;
    color: #7a7a7a;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 3px;
    font-size: 0.75rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    transition: all 0.2s ease-in-out;
}

.clear-button:hover {
    background-color: #f5f5f5;
    color: #363636;
}

.clear-button:focus {
    outline: none;
    box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
}

.multi-select-options {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 99999;
    margin-top: 0.25rem;
    box-sizing: border-box;
    display: block;
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
}

.multi-select-search {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #f0f0f0;
    background-color: #fafafa;
}

.multi-select-search input {
    border: 1px solid #dbdbdb;
    border-radius: 3px;
    outline: none;
    width: 100%;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    box-sizing: border-box;
}

.multi-select-search input:focus {
    border-color: #3273dc;
    box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
}

.multi-select-list {
    padding: 0.25rem 0;
}

.multi-select-option {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    box-sizing: border-box;
}

.multi-select-option:hover {
    background-color: #f5f5f5;
}

.multi-select-option input[type="checkbox"] {
    margin-right: 0.75rem;
    transform: scale(0.9);
    cursor: pointer;
    flex-shrink: 0;
    accent-color: #000000;
    /* Black color */
}

/* Custom checkbox styling for black color */
.multi-select-option input[type="checkbox"]:checked {
    background-color: #000000;
    border-color: #000000;
}

.multi-select-option input[type="checkbox"]:focus {
    border-color: #000000;
    box-shadow: 0 0 0 0.125em rgba(0, 0, 0, 0.25);
}

.multi-select-option .option-text {
    flex-grow: 1;
    font-size: 0.875rem;
    color: #363636;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Scrollbar styling for the options list */
.multi-select-options::-webkit-scrollbar {
    width: 6px;
}

.multi-select-options::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.multi-select-options::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.multi-select-options::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Selected items container */
.selected-items-container {
    max-height: 140px;
    overflow-y: auto;
    padding-right: 5px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    margin-top: 0.5rem;
    overflow-x: hidden;
    /* Prevent horizontal overflow */
}

.selected-items-container::-webkit-scrollbar {
    display: none;
}

.selected-items-container {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

/* Tags styling for selected items */
.selected-items-container .tags {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    /* Prevent overflow */
    box-sizing: border-box;
    padding: 0;
    margin: 0;
}

.selected-items-container .tag {
    max-width: calc(100% - 0.5rem);
    /* Account for gap */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
    word-break: break-all;
    /* Break long words */
    font-size: 0.75rem;
    /* Smaller font size */
    padding: 0.25rem 0.5rem;
    /* Smaller padding */
    line-height: 1.2;
    /* Tighter line height */
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    /* Allow shrinking */
    max-width: 100%;
    /* Ensure it doesn't exceed container */
    width: auto;
    /* Allow natural width */
}

/* Override Bulma tag styles */
.selected-items-container .tag.is-success {
    max-width: 100% !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    word-break: break-all !important;
    font-size: 0.75rem !important;
    padding: 0.25rem 0.5rem !important;
    line-height: 1.2 !important;
    box-sizing: border-box !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    min-width: 0 !important;
    flex-shrink: 0 !important;
    width: auto !important;
}

/* Ensure delete button in tag doesn't cause overflow */
.selected-items-container .tag .delete {
    flex-shrink: 0;
    margin-left: 0.25rem;
}

/* Ensure the control div doesn't overflow */
.control {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: visible;
    position: relative;
}

/* Ensure proper alignment with modal constraints */
@media (max-width: 450px) {
    .global-selector-container {
        max-width: 100%;
    }

    .multi-select-dropdown {
        font-size: 0.8rem;
        padding: 0.25rem 0.5rem;
    }

    .multi-select-display {
        margin-right: 0.25rem;
    }
}

/* Additional constraints for modal compatibility */
.global-selector-container {
    max-width: 100%;
    /* Inherit parent width */
}

.multi-select-container {
    max-width: 100%;
    /* Inherit parent width */
}

.multi-select-dropdown {
    max-width: 100%;
    /* Inherit parent width */
}

.multi-select-options {
    max-width: 100%;
    /* Inherit parent width */
}

.selected-items-container {
    max-width: 100%;
    /* Inherit parent width */
}

.selected-items-container .tags {
    max-width: 100%;
    /* Inherit parent width */
}
</style>
